import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function MassToMolesConverter() {
  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Mass to Moles Converter", url: "https://calclab.app/calculator/mass-to-moles" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Mass to Moles Converter",
      "Convert mass to moles using molar mass. Free online chemistry stoichiometry calculator.",
      "https://calclab.app/calculator/mass-to-moles",
      lang
    )
  ]);
  const [mass, setMass] = useState("");
  const [molarMass, setMolarMass] = useState("");
  const [result, setResult] = useState<number | null>(null);

  const calculate = () => {
    const m = parseFloat(mass);
    const mm = parseFloat(molarMass);

    if (isNaN(m) || isNaN(mm) || mm <= 0) {
      toast({
        title: "Invalid Input",
        description: "Please enter valid positive numbers.",
        variant: "destructive",
      });
      return;
    }

    const moles = m / mm;
    setResult(moles);

    toast({
      title: "Calculation Complete",
      description: `Moles: ${moles.toFixed(4)} mol`,
    });
  };

  const reset = () => {
    setMass("");
    setMolarMass("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Mass to Moles Converter" 
      calculatorPath="/calculator/mass-to-moles"
      category="chemistry"
      subcategory="Stoichiometry"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Mass to Moles Converter</CardTitle>
            <CardDescription>
              Convert mass to moles using molar mass
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="mass">Mass (g)</Label>
                <Input
                  id="mass"
                  type="number"
                  step="any"
                  value={mass}
                  onChange={(e) => setMass(e.target.value)}
                  placeholder="Enter mass"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="molarMass">Molar Mass (g/mol)</Label>
                <Input
                  id="molarMass"
                  type="number"
                  step="any"
                  value={molarMass}
                  onChange={(e) => setMolarMass(e.target.value)}
                  placeholder="Enter molar mass"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Calculate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result !== null && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Result</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start justify-between gap-4">
                    <p className="text-2xl font-bold break-all overflow-wrap-anywhere flex-1">{result.toFixed(4)} mol</p>
                    <CopyResultButton value={`${result.toFixed(4)} mol`} />
                  </div>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Formula</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-mono">n = m / M</p>
                <p className="text-sm text-muted-foreground mt-2">
                  where n = moles, m = mass (g), M = molar mass (g/mol)
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
